import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Stack,
  Divider,
  Chip,
  TextField,
  InputAdornment,
  Button,
} from "@mui/material";
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  ArrowForward as ArrowForwardIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { format, differenceInHours, differenceInDays } from "date-fns";
import { apiClient } from "../../utils/apiClient";
import DobbyDemoPage from "../DobbyDemoPage";
import type { Reminder } from "@services/demo/msoffice/types";
import type { ChatSource } from "@services/chat/types";
import { ShipmentDetailDrawer } from "./ShipmentDetailDrawer";
import type { DrawerShipment } from "./ShipmentDetailDrawer";

// Shipment type matching the demo API response
interface DemoShipment extends DrawerShipment {
  id: string;
  name: string;
  properties: Array<{ name: string; value: string }>;
}

interface ReminderSection {
  type: "separator" | "reminders";
  label?: string;
  reminders?: Reminder[];
}

// Helper to extract a property value from a shipment
const getPropertyValue = (shipment: DemoShipment, propertyName: string): string => {
  const prop = shipment.properties.find((p) => p.name === propertyName);
  return prop?.value || "";
};

// Shipment overview tile shown alongside reminder cards
function ShipmentOverviewTile({
  shipment,
  onOpenShipment,
}: {
  shipment: DemoShipment;
  onOpenShipment?: () => void;
}) {
  const pol = getPropertyValue(shipment, "port_of_loading");
  const pod = getPropertyValue(shipment, "port_of_discharge");
  const booking = getPropertyValue(shipment, "booking_number");
  const container = getPropertyValue(shipment, "container_number");

  return (
    <Stack spacing={0.25}>
      {onOpenShipment && (
        <Box
          component="button"
          onClick={onOpenShipment}
          sx={{
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            textAlign: "left",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            color: "#1976d2",
            "&:hover": { color: "#1565c0" },
          }}
        >
          <OpenInNewIcon sx={{ fontSize: 14 }} />
          <Typography variant="body2" sx={{ fontSize: "0.8rem", fontWeight: 500 }}>
            Open Shipment
          </Typography>
        </Box>
      )}
      {(pol || pod) && (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
            {pol || "\u2014"}
          </Typography>
          <ArrowForwardIcon sx={{ fontSize: 12, color: "text.secondary" }} />
          <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
            {pod || "\u2014"}
          </Typography>
        </Stack>
      )}
      {booking && (
        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
          Booking #: {booking}
        </Typography>
      )}
      {container && (
        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
          Container: {container}
        </Typography>
      )}
    </Stack>
  );
}

// Reminder card component
function DemoReminderCard({
  reminder,
  shipment,
  onResolve,
  onShipmentClick,
}: {
  reminder: Reminder;
  shipment?: DemoShipment;
  onResolve: (id: string) => void;
  onShipmentClick?: (shipmentId: string) => void;
}) {
  if (reminder.status === "resolved") return null;

  return (
    <Box
      sx={{
        border: "1px solid #ffa726",
        borderRadius: "8px",
        backgroundColor: "#fff3e0",
        px: 2,
        py: 1.5,
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <WarningIcon sx={{ fontSize: "1.5rem", color: "#f57c00" }} />

      <Box sx={{ width: "1px", height: "100%", backgroundColor: "#ffa726", alignSelf: "stretch" }} />

      <Box sx={{ minWidth: "90px" }}>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontSize: "0.7rem", fontWeight: 600, display: "block" }}
        >
          Created on
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.775rem" }}>
          {reminder.triggerTime && format(new Date(reminder.triggerTime), "MMM d, yyyy")}
        </Typography>
      </Box>

      <Box sx={{ width: "1px", height: "100%", backgroundColor: "#ffa726", alignSelf: "stretch" }} />

      <Box sx={{ width: "400px" }}>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
          {reminder.name}
        </Typography>
        {reminder.description && (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontSize: "0.75rem", display: "block", mt: 0.25 }}
          >
            {reminder.description}
          </Typography>
        )}
      </Box>

      {shipment && (
        <>
          <Box sx={{ width: "1px", height: "100%", backgroundColor: "#ffa726", alignSelf: "stretch" }} />
          <Box sx={{ minWidth: "200px" }}>
            <ShipmentOverviewTile
              shipment={shipment}
              onOpenShipment={
                onShipmentClick && reminder.shipmentId
                  ? () => onShipmentClick(reminder.shipmentId!)
                  : undefined
              }
            />
          </Box>
        </>
      )}

      {reminder.assignedTo && (
        <>
          <Box sx={{ width: "1px", height: "100%", backgroundColor: "#ffa726", alignSelf: "stretch" }} />
          <Box sx={{ minWidth: "120px" }}>
            <Chip
              label={reminder.assignedTo}
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.75rem", height: "24px" }}
            />
          </Box>
        </>
      )}

      <Box sx={{ flex: 1 }} />

      <Button
        size="small"
        variant="outlined"
        onClick={() => onResolve(reminder.id)}
        startIcon={<CheckCircleIcon sx={{ fontSize: "1rem", color: "success.main" }} />}
        sx={{
          textTransform: "none",
          whiteSpace: "nowrap",
          minWidth: "fit-content",
          fontSize: "0.8125rem",
          py: 0.5,
          px: 1.5,
          borderColor: "#f57c00",
          color: "#f57c00",
          "&:hover": {
            borderColor: "#ef6c00",
            backgroundColor: "rgba(245, 124, 0, 0.04)",
          },
        }}
      >
        Resolve
      </Button>
    </Box>
  );
}

export const DemoRemindersDashboard: React.FC = () => {
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [shipments, setShipments] = useState<DemoShipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Drawer state
  const [selectedShipment, setSelectedShipment] = useState<DemoShipment | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [defaultChatAnswer, setDefaultChatAnswer] = useState<{
    dobbyResponse: string;
    sources?: ChatSource[];
  } | null>(null);

  // Fetch reminders, shipments, and chat answer on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [remindersRes, shipmentsRes, chatRes] = await Promise.all([
          apiClient.get<{ data: Reminder[] }>("/demo-shipment-feed/reminders"),
          apiClient.get<{ shipments: DemoShipment[] }>("/demo-shipment-feed/shipments"),
          apiClient.get<{ dobbyResponse: string; sources?: ChatSource[] }>("/demo-shipment-feed/chat-answer"),
        ]);
        setReminders(remindersRes.data);
        setShipments(shipmentsRes.shipments);
        setDefaultChatAnswer(chatRes);
      } catch (error) {
        console.error("Error fetching demo data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Build shipment lookup map
  const shipmentMap = useMemo(() => {
    const map = new Map<string, DemoShipment>();
    shipments.forEach((s) => map.set(s.id, s));
    return map;
  }, [shipments]);

  // Filter reminders by search query
  const filteredReminders = useMemo(() => {
    const pending = reminders.filter((r) => r.status === "pending");
    if (!searchQuery.trim()) return pending;
    const q = searchQuery.toLowerCase();
    return pending.filter((r) => {
      const searchable = [r.name, r.description, r.assignedTo, r.type].join(" ").toLowerCase();
      return searchable.includes(q);
    });
  }, [reminders, searchQuery]);

  // Organize into time-based sections
  const reminderSections = useMemo(() => {
    const now = new Date();
    const sections: ReminderSection[] = [];
    let currentSection: Reminder[] = [];
    let hasShownOneDaySeparator = false;
    let hasShownOneWeekSeparator = false;

    filteredReminders.forEach((reminder) => {
      if (!reminder.triggerTime) {
        currentSection.push(reminder);
        return;
      }

      const hoursSince = differenceInHours(now, new Date(reminder.triggerTime));
      const daysSince = differenceInDays(now, new Date(reminder.triggerTime));

      if (!hasShownOneWeekSeparator && daysSince >= 7) {
        if (currentSection.length > 0) {
          sections.push({ type: "reminders", reminders: currentSection });
          currentSection = [];
        }
        sections.push({ type: "separator", label: "More than a week ago" });
        hasShownOneWeekSeparator = true;
        hasShownOneDaySeparator = true;
      } else if (!hasShownOneDaySeparator && hoursSince >= 24) {
        if (currentSection.length > 0) {
          sections.push({ type: "reminders", reminders: currentSection });
          currentSection = [];
        }
        sections.push({ type: "separator", label: "More than one day ago" });
        hasShownOneDaySeparator = true;
      }

      currentSection.push(reminder);
    });

    if (currentSection.length > 0) {
      sections.push({ type: "reminders", reminders: currentSection });
    }

    return sections;
  }, [filteredReminders]);

  const handleResolve = useCallback((reminderId: string) => {
    setReminders((prev) =>
      prev.map((r) =>
        r.id === reminderId
          ? { ...r, status: "resolved", resolvedTime: new Date().toISOString() }
          : r,
      ),
    );
  }, []);

  // Open the shipment detail drawer
  const handleShipmentClick = useCallback(
    (shipmentId: string) => {
      const shipment = shipmentMap.get(shipmentId);
      if (shipment) {
        setSelectedShipment(shipment);
        setDrawerOpen(true);
      }
    },
    [shipmentMap],
  );

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  return (
    <DobbyDemoPage fullHeight>
      <Box sx={{ p: 3, overflow: "auto", height: "100%" }}>
        {/* Search bar */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              size="small"
              placeholder="Search reminders..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 350 }}
            />
            <Typography variant="body2" color="text.secondary">
              {filteredReminders.length} pending reminders
            </Typography>
          </Stack>
        </Box>

        {/* Loading */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {filteredReminders.length > 0 && (
              <Box sx={{ mb: 4 }}>
                {reminderSections.map((section, idx) => {
                  if (section.type === "separator") {
                    return (
                      <Box
                        key={`separator-${idx}`}
                        sx={{ display: "flex", alignItems: "center", my: 3 }}
                      >
                        <Divider sx={{ flex: 1 }} />
                        <Typography
                          variant="caption"
                          sx={{
                            mx: 2,
                            color: "text.secondary",
                            fontWeight: 500,
                            textTransform: "uppercase",
                            fontSize: "0.7rem",
                          }}
                        >
                          {section.label}
                        </Typography>
                        <Divider sx={{ flex: 1 }} />
                      </Box>
                    );
                  }

                  return (
                    <Box
                      key={`reminders-${idx}`}
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {section.reminders?.map((reminder) => (
                        <DemoReminderCard
                          key={reminder.id}
                          reminder={reminder}
                          shipment={
                            reminder.shipmentId
                              ? shipmentMap.get(reminder.shipmentId)
                              : undefined
                          }
                          onResolve={handleResolve}
                          onShipmentClick={handleShipmentClick}
                        />
                      ))}
                    </Box>
                  );
                })}
              </Box>
            )}

            {filteredReminders.length === 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No pending reminders found
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      <ShipmentDetailDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        shipment={selectedShipment}
        allReminders={reminders}
        defaultChatAnswer={defaultChatAnswer}
      />
    </DobbyDemoPage>
  );
};
